import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from './useAuth';

export const useProfileSwitch = () => {
  const [activeProfile, setActiveProfile] = useState<'aluno' | 'demo' | null>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  // Buscar perfil ativo ao carregar
  useEffect(() => {
    const fetchActiveProfile = async () => {
      if (!user) return;
      
      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('role')
          .eq('user_id', user.id)
          .single();
          
        if (error) throw error;
        
        if (profile?.role === 'demo' || profile?.role === 'aluno') {
          setActiveProfile(profile.role);
        }
      } catch (error) {
        console.error('Error fetching active profile:', error);
      }
    };

    fetchActiveProfile();
  }, [user]);

  // Alternar perfil
  const switchProfile = async (newProfile: 'aluno' | 'demo') => {
    if (loading || !user) return;
    
    setLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ role: newProfile, updated_at: new Date().toISOString() })
        .eq('user_id', user.id);

      if (error) throw error;

      setActiveProfile(newProfile);
      
      toast({
        title: "Perfil alterado",
        description: `Agora você está visualizando como ${newProfile === 'demo' ? 'Administrador (Demo)' : 'Aluno'}`,
      });

      // Recarregar a página para aplicar as mudanças
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      console.error('Error switching profile:', error);
      toast({
        title: "Erro ao alterar perfil",
        description: "Não foi possível alterar o perfil. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Verificar se é usuário demo
  const isDemoUser = async () => {
    if (!user) return false;
    
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('user_id', user.id)
        .single();
        
      if (error) throw error;
      return profile?.role === 'demo';
    } catch (error) {
      console.error('Error checking demo user:', error);
      return false;
    }
  };

  // Verificar se pode executar ações de IA
  const canExecuteAI = async () => {
    if (!user) return false;
    
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('user_id', user.id)
        .single();
        
      if (error) throw error;
      return profile?.role === 'admin' || profile?.role === 'editor';
    } catch (error) {
      console.error('Error checking AI permissions:', error);
      return false;
    }
  };

  return {
    activeProfile,
    switchProfile,
    loading,
    isDemoUser,
    canExecuteAI,
  };
};
